import { Box, BoxProps } from "@chakra-ui/react"
import { requestAugmentationPanelAdd } from "@pathwright/ux/src/components/augmentation/panelAddRequest"
import { augmentationSectionFocusTarget } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import { type IconName } from "@pathwright/pathicons"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Placeholder, {
  PlaceholderRootProps
} from "@pathwright/ux/src/components/placeholder/Placeholder"
import { Tooltip } from "@pathwright/ux/src/components/chakra/ui/tooltip"
import { ReactNode, RefObject } from "react"
import { useLocation } from "react-router-dom"
import { useCoverPageIsEditable } from "../access"
import { useCoverPageContext } from "../CoverPageProvider"
import { CoverPageBlankSlateKind } from "../blankSlates"
import { COVER_PAGE_PADDING } from "../constants"
import CoverPageDivider from "../CoverPageDivider"
import { highlightCoverPageSection } from "../coverPageHashNavigation"
import { CoverPagePanelSectionKey, toCoverPageScrollHashKey } from "../types"
import { COVER_PAGE_REVEAL_IN_SECTION, coverPageRevealOnHoverCss } from "../ui"
import { useCoverPageTranslate, useGetPathUrl } from "../utils"
import { useCoverPageAddLabel } from "./addLabels"
import { requestCoverPagePanelFieldFocus } from "./panelFieldFocus"
import { useCoverPageBlankSlate } from "./useCoverPageBlankSlate"
import { getCoverPagePanelPath, getCoverPagePanelSectionLinkTo } from "./utils"

const sections: Record<CoverPageBlankSlateKind, CoverPagePanelSectionKey> = {
  offer: "offers",
  pitch: "header",
  preview: "preview",
  overview: "description",
  contributors: "contributors",
  categories: "categories"
}
// Gaps filled from a picker on the panel's home list, by that add row's key.
const panelAddItemKeys: Partial<Record<CoverPageBlankSlateKind, string>> = {
  contributors: "add-contributor",
  categories: "add-category"
}

// One line on why each gap matters; the label comes from the shared add labels.
const hints: Record<CoverPageBlankSlateKind, string> = {
  offer: "Give visitors a way to join this Path.",
  pitch: "Give visitors a one-line reason to keep reading.",
  preview: "Let visitors try a few Steps before they join.",
  overview: "Explain what visitors will learn and why it matters.",
  contributors: "Show visitors who created or teaches this Path.",
  categories: "Help visitors find this Path in your Library."
}

type CoverPageBlankSlateDependency = "preview_unpublished"

// Shown in place of a gap's own prompt when something else has to exist
// first. The label says what is missing; the slot links to where to fix it.
const dependencyCopy: Record<
  CoverPageBlankSlateDependency,
  { label: string; icon: IconName }
> = {
  preview_unpublished: {
    label: "Publish at least one step to reveal the path preview",
    icon: "path"
  }
}

/**
 * What a gap is waiting on, if anything, and where to resolve it. Add a case
 * here when a gap cannot be filled from its own editor yet.
 */
export function useCoverPageBlankSlateDependency(
  kind: CoverPageBlankSlateKind
) {
  const canPreview = useCoverPageContext((v) => v.canPreview)
  const pathUrl = useGetPathUrl()
  const { t } = useCoverPageTranslate()
  // Preview Steps can only be chosen from published Path content.
  const dependency: CoverPageBlankSlateDependency | null =
    kind === "preview" && !canPreview ? "preview_unpublished" : null
  if (!dependency) return null
  const text = dependencyCopy[dependency]
  return {
    key: dependency,
    icon: text.icon,
    label: t(`blank_slates.dependencies.${dependency}.label`, {
      defaultValue: text.label
    }),
    // The label already says what is missing.
    hint: null,
    to: pathUrl
  }
}

/** A gap's label and one-line hint, translated. */
export function useCoverPageBlankSlateText(kind: CoverPageBlankSlateKind) {
  const { t } = useCoverPageTranslate()
  return {
    label: useCoverPageAddLabel(kind),
    hint: t(`blank_slates.${kind}.body`, { defaultValue: hints[kind] })
  }
}

/**
 * Where "add this kind of content" goes, and what to do on the way: every
 * kind lands one step in (a form, a focused field, or an open picker).
 */
function useCoverPageAddAction(kind: CoverPageBlankSlateKind) {
  const { pathname } = useLocation()
  const section = sections[kind]
  const to =
    kind === "offer"
      ? {
          pathname,
          search: getCoverPagePanelPath("offers/new"),
          hash: "#offers"
        }
      : getCoverPagePanelSectionLinkTo(pathname, section)
  const onClick = () => {
    highlightCoverPageSection(section, { highlightMain: false })
    if (kind === "pitch") requestCoverPagePanelFieldFocus("short_description")
    const addItemKey = panelAddItemKeys[kind]
    if (addItemKey) requestAugmentationPanelAdd(addItemKey)
  }
  return { to, onClick }
}

type CoverPageBlankSlateProps = Omit<PlaceholderRootProps, "onClick"> & {
  kind: CoverPageBlankSlateKind
  /** Edit in place instead of opening the section's panel. */
  onClick?: () => void
  /** A shape standing in for the section's image or avatar. */
  media?: ReactNode
}

/**
 * The Placeholder for one Cover Page gap: the whole slot opens the gap's
 * editor. Pass Box style props to shape it like the content that fills it.
 */
export default function CoverPageBlankSlate({
  kind,
  onClick,
  media,
  ...rootProps
}: CoverPageBlankSlateProps) {
  const show = useCoverPageBlankSlate(kind)
  const text = useCoverPageBlankSlateText(kind)
  const dependency = useCoverPageBlankSlateDependency(kind)
  const add = useCoverPageAddAction(kind)
  if (!show) return null
  const { label, hint } = dependency ?? text
  const to = dependency ? dependency.to : add.to
  const handleClick = () => {
    if (dependency) return
    add.onClick()
    onClick?.()
  }
  // Inline shapes have no room for the hint, so they keep it as a tooltip.
  const isInline = rootProps.shape === "pill" || rootProps.shape === "text"
  const labelNode = (
    // A dependency carries an icon above its text; a plain prompt is text only.
    <Placeholder.Label flexDirection={dependency ? "column" : undefined}>
      {dependency ? <Pathicon icon={dependency.icon} size="1.5em" /> : null}
      {label}
    </Placeholder.Label>
  )
  const content = (
    <>
      {media}
      {hint && !isInline ? (
        <Placeholder.Body>
          {labelNode}
          <Placeholder.Hint>{hint}</Placeholder.Hint>
        </Placeholder.Body>
      ) : (
        labelNode
      )}
    </>
  )
  const slot = (
    <Placeholder.Root
      data-cover-blank-slate={kind}
      data-cover-blank-slate-dependency={dependency?.key}
      variant={dependency ? "notice" : "action"}
      {...augmentationSectionFocusTarget()}
      {...rootProps}
      // Nowhere to send the designer: the slot still explains the gap.
      cursor={to ? undefined : "default"}
      asChild
    >
      {!to ? (
        <div>{content}</div>
      ) : onClick && !dependency ? (
        <button type="button" onClick={handleClick}>
          {content}
        </button>
      ) : (
        <Link to={to} onClick={handleClick}>
          {content}
        </Link>
      )}
    </Placeholder.Root>
  )
  return isInline && hint ? (
    <Tooltip content={hint} openDelay={400}>
      {slot}
    </Tooltip>
  ) : (
    slot
  )
}

/** The kinds of content a Cover Page can hold more than one of. */
export type CoverPageAddAnotherKind = Extract<
  CoverPageBlankSlateKind,
  "offer" | "preview" | "contributors" | "categories"
>

/**
 * A quiet "add another" Placeholder for a section that already has content:
 * a round plus where the next item would go, named by a tooltip. It only
 * appears while the editor hovers or focuses the section, like the section's
 * edit control, so a finished page looks finished. With no hover (touch
 * screens) it is always shown.
 *
 * Render it inside the section's `role="group"` container.
 */
export function CoverPageAddAnother({
  kind,
  ...boxProps
}: BoxProps & { kind: CoverPageAddAnotherKind }) {
  const isEditable = useCoverPageIsEditable()
  const resourceIsReady = useCoverPageContext((v) => v.resourceIsReady)
  const offersAreReady = useCoverPageContext((v) => v.offersAreReady)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)
  const missing = useCoverPageContext((v) => v.blankSlateGaps[kind])
  const label = useCoverPageAddLabel(kind)
  const add = useCoverPageAddAction(kind)
  const show =
    isEditable &&
    !immersiveActive &&
    resourceIsReady &&
    (kind !== "offer" || offersAreReady) &&
    // An empty section shows its blank slate instead.
    !missing
  if (!show) return null
  return (
    <Box
      display="inline-flex"
      css={coverPageRevealOnHoverCss(COVER_PAGE_REVEAL_IN_SECTION)}
      {...boxProps}
    >
      <Tooltip content={label} openDelay={600}>
        <Placeholder.Root
          shape="icon"
          aria-label={label}
          data-cover-add-another={kind}
          asChild
        >
          <Link to={add.to} onClick={add.onClick}>
            <Pathicon icon="plus" />
          </Link>
        </Placeholder.Root>
      </Tooltip>
    </Box>
  )
}

/**
 * The header's Join button before any Offer exists. It keeps the button's
 * place in the row, reads as unavailable, and takes the designer to the empty
 * Offers section.
 */
export function CoverPageJoinPlaceholder() {
  const show = useCoverPageBlankSlate("offer")
  const offersSectionRef = useCoverPageContext((v) => v.offersSectionRef)
  const { t } = useCoverPageTranslate()
  if (!show) return null
  return (
    <Tooltip
      content={t("blank_slates.offer.join_hint", {
        defaultValue: "Add an Offer so visitors can join."
      })}
      showArrow
    >
      <Placeholder.Root
        shape="pill"
        tone="onImage"
        minW="200px"
        opacity={0.55}
        _hover={{ opacity: 0.8 }}
        data-cover-blank-slate="join"
        asChild
      >
        <button
          type="button"
          onClick={() =>
            offersSectionRef?.current?.scrollIntoView({
              behavior: "smooth",
              block: "start"
            })
          }
        >
          <Placeholder.Label>{t("join")}</Placeholder.Label>
        </button>
      </Placeholder.Root>
    </Tooltip>
  )
}

/**
 * An editable empty section in its real frame: the same divider, heading,
 * scroll target, and padding as the filled section, with its Placeholder.
 */
export function CoverPageBlankSlateSection({
  kind,
  title,
  sectionRef,
  ...slotProps
}: Omit<CoverPageBlankSlateProps, "title"> & {
  /** The section heading shown on the filled page. */
  title: string
  sectionRef?: RefObject<HTMLDivElement>
}) {
  const show = useCoverPageBlankSlate(kind)
  if (!show) return null
  return (
    <Box
      id={toCoverPageScrollHashKey(sections[kind])}
      ref={sectionRef}
      position="relative"
      role="group"
      w="full"
      scrollMarginTop="150px"
    >
      <CoverPageDivider title={title} titleMargin="1rem" />
      <Box w="full" px={COVER_PAGE_PADDING}>
        <CoverPageBlankSlate kind={kind} {...slotProps} />
      </Box>
    </Box>
  )
}
